import { cloneElement, useEffect, useRef, useState } from 'react';
import { WS2_PROPERTY_TYPES, WS2_SURFACE_TYPES } from '../../../../constants/amenities';
import {
    hasUploadProvenance,
    isAiFieldDirty,
    isUserProvidedValue,
    normalize,
} from '../drawerEditHelpers';
import { flattenInstructions } from '../../../../utils/parkingInstructions';

const FIELD_ORDER = ['name', 'type', 'instructions', 'entrance', 'surface'];

const FIELD_LABELS = {
    name: 'Property Name',
    type: 'Property Type',
    instructions: 'Parking Instructions',
    entrance: 'Primary Entrance',
    surface: 'Parking Surface',
};

// Directions may be saved as structured approaches (PP-492) or hold the
// PP-488 "coming soon" sentence (#405); show them as the text a host reads,
// never "[object Object]" or the sentence. Other fields are unchanged.
function instructionsText(value) {
    return flattenInstructions(value) || null;
}

export function buildFieldList(listing) {
    const upload = listing.uploadFields || {};
    const enrichment = listing.enrichmentFields || {};
    const confirmed = listing.confirmedFields || {};

    const fields = [];
    for (const key of FIELD_ORDER) {
        const uploadVal = upload[key];
        const hasUpload = uploadVal != null && String(uploadVal).trim() !== '';
        const enrichField = enrichment[key];
        const enrichVal = key === 'instructions'
            ? instructionsText(enrichField?.value ?? enrichField)
            : (enrichField?.value ?? (typeof enrichField === 'string' ? enrichField : null));
        const hasEnrich = enrichVal != null && enrichVal !== '';
        const confirmedVal = key === 'instructions' ? instructionsText(confirmed[key]) : confirmed[key];
        const hasConfirmed = confirmedVal != null && confirmedVal !== '';

        if (!hasUpload && !hasEnrich && !hasConfirmed) continue;

        fields.push({
            key,
            label: FIELD_LABELS[key],
            uploadSource: hasUpload,
            aiValue: hasEnrich ? String(enrichVal) : null,
            uploadValue: hasUpload ? String(uploadVal) : null,
            confidence: hasEnrich
                ? (enrichField?.confidence || 'medium')
                : (hasUpload ? 'upload' : 'medium'),
            displayValue: String(confirmedVal ?? (hasUpload ? uploadVal : null) ?? enrichVal ?? ''),
        });
    }

    return fields.sort((a, b) => {
        if (a.uploadSource && !b.uploadSource) return -1;
        if (!a.uploadSource && b.uploadSource) return 1;
        return FIELD_ORDER.indexOf(a.key) - FIELD_ORDER.indexOf(b.key);
    });
}

const FlatCheck = () => (
    <svg className="flat-check" width="10" height="10" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.4" style={{ verticalAlign: '-1px' }}>
        <polyline points="2,7 6,11 12,3" />
    </svg>
);

function resolveCardSurfaceClass(state, field) {
    if (state === 'upload') return 'confidence-upload';
    if (state === 'confirmed' || state === 'edited') return 'confirmed';
    if (field.confidence === 'low') return 'confidence-low';
    if (field.confidence === 'medium') return 'confidence-medium';
    if (field.confidence === 'high') return 'confidence-high';
    return '';
}

function inferViewState(field, listing) {
    const current = field.displayValue;
    const uploadVal = listing.uploadFields?.[field.key];
    const enrichField = listing.enrichmentFields?.[field.key];
    const aiBaseline = enrichField?.value ?? (typeof enrichField === 'string' ? enrichField : null);
    const confirmed = listing.confirmedFields?.[field.key];

    if (uploadVal != null && normalize(uploadVal) === normalize(current) && current !== '') {
        return 'upload';
    }
    if (current !== '' && aiBaseline != null && normalize(current) !== normalize(aiBaseline)) {
        return 'edited';
    }
    if (confirmed != null && confirmed !== '') return 'confirmed';
    if (enrichField?.confidence === 'high' && aiBaseline != null && current !== '') return 'confirmed';
    return 'pending';
}

const FIELD_ARIA_LABELS = {
    name: 'Property name',
    type: 'Property type',
    instructions: 'Parking instructions',
    hours: 'Hours of operation',
    entrance: 'Primary entrance',
    surface: 'Parking surface',
};

function renderFieldInput({ fieldKey, value, onChange }) {
    const ariaLabel = FIELD_ARIA_LABELS[fieldKey] || 'Edit value';
    switch (fieldKey) {
        case 'type':
            return (
                <select
                    aria-label={ariaLabel}
                    className="host-edit-select"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                >
                    <option value="" disabled>Select property type</option>
                    {WS2_PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
            );
        case 'surface':
            return (
                <select
                    aria-label={ariaLabel}
                    className="host-edit-select"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                >
                    <option value="" disabled>Select surface type</option>
                    {WS2_SURFACE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
            );
        case 'instructions':
            return (
                <textarea
                    aria-label={ariaLabel}
                    className="host-edit-textarea"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
        default:
            return (
                <input
                    type="text"
                    aria-label={ariaLabel}
                    className="host-edit-input"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                />
            );
    }
}

export default function AiFieldsSection({
    listing,
    editMode = false,
    dirtyFields,
    baselineFields,
    setDirtyFields,
    fieldErrors = {},
    onRequestEditWithSeed,
}) {
    const fields = buildFieldList(listing);
    const [editingKey, setEditingKey] = useState(null);
    const syncDirtyRef = useRef(null);

    useEffect(() => {
        syncDirtyRef.current = dirtyFields ?? null;
    }, [dirtyFields]);

    if (!fields.length) return null;

    const safePatch = (mutator) => {
        const base = dirtyFields ?? syncDirtyRef.current;
        if (!base) {
            const seed = onRequestEditWithSeed?.();
            if (!seed) return;
            syncDirtyRef.current = seed;
            const next = mutator(seed);
            syncDirtyRef.current = next;
            setDirtyFields?.(next);
            return;
        }
        setDirtyFields?.((prev) => {
            const resolved = prev ?? syncDirtyRef.current;
            if (!resolved) return prev;
            const next = mutator(resolved);
            syncDirtyRef.current = next;
            return next;
        });
    };

    const patchAiField = (key, value) => {
        safePatch((state) => ({
            ...state,
            aiFields: { ...state.aiFields, [key]: value },
        }));
    };

    const patchAiFieldAndState = (key, value, newState) => {
        safePatch((state) => ({
            ...state,
            aiFields: { ...state.aiFields, [key]: value },
            aiFieldStates: { ...state.aiFieldStates, [key]: newState },
        }));
    };

    const getFieldState = (key) => {
        if (dirtyFields?.aiFieldStates && Object.prototype.hasOwnProperty.call(dirtyFields.aiFieldStates, key)) {
            return dirtyFields.aiFieldStates[key];
        }
        const field = fields.find((f) => f.key === key);
        return field ? inferViewState(field, listing) : 'pending';
    };

    const getFieldValue = (key, fallback) => {
        if (dirtyFields?.aiFields && Object.prototype.hasOwnProperty.call(dirtyFields.aiFields, key)) {
            return dirtyFields.aiFields[key];
        }
        return fallback;
    };

    const handleConfirm = (key, field) => {
        const value = getFieldValue(key, field.displayValue);
        patchAiFieldAndState(key, value, 'confirmed');
    };

    const AiFieldEditor = ({ fieldKey, editValue, onChange, onCancel, onSave }) => {
        const input = renderFieldInput({ fieldKey, value: editValue, onChange });
        const actionRow = (
            <div className="host-ai-field-actions">
                <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={onSave}>Save</button>
            </div>
        );

        return (
            <>
                {cloneElement(input, { autoFocus: true })}
                {actionRow}
            </>
        );
    };

    const renderEditCard = (field) => {
        const key = field.key;
        const state = getFieldState(key);
        const displayValue = getFieldValue(key, field.displayValue);
        const edited = isAiFieldDirty(baselineFields, dirtyFields, key);
        const isEditing = editingKey === key;
        const confidenceClass = resolveCardSurfaceClass(state, field);
        const uploadOrigin = hasUploadProvenance(listing, key);
        const userProvided = isUserProvidedValue(listing, key, displayValue);
        const showConfirm = state === 'pending' && !userProvided;
        const canEditThisField = editMode || state === 'pending';

        return (
            <div key={key} className={`host-ai-field ${confidenceClass}`}>
                <div className="host-ai-field-head">
                    <span className="host-ai-field-label">{field.label}</span>
                    {uploadOrigin && (
                        <span className="badge-upload-source">From Upload Document</span>
                    )}
                    {state === 'confirmed' && !edited && (
                        <span className="badge-confirmed"><FlatCheck /> Confirmed</span>
                    )}
                    {(state === 'edited' || (edited && state !== 'upload')) && (
                        <span className="badge-edited">Edited</span>
                    )}
                    {state === 'pending' && !isEditing && (
                        <span className={`confidence-badge ${field.confidence}`}>
                            {field.confidence === 'high' ? 'High' : field.confidence === 'low' ? 'Low' : 'Medium'}
                        </span>
                    )}
                </div>

                {isEditing ? (
                    <AiFieldEditor
                        fieldKey={key}
                        editValue={displayValue}
                        onChange={(v) => patchAiField(key, v)}
                        onCancel={() => {
                            patchAiField(key, baselineFields?.aiFields?.[key] ?? field.displayValue);
                            setEditingKey(null);
                        }}
                        onSave={() => {
                            const v = String(displayValue).trim();
                            if (!v) return;

                            const enrichField = listing.enrichmentFields?.[key];
                            const aiBaseline = enrichField?.value
                                ?? (typeof enrichField === 'string' ? enrichField : null);
                            const uploadVal = listing.uploadFields?.[key];

                            let nextState;
                            if (aiBaseline != null && normalize(aiBaseline) === normalize(v)) {
                                nextState = 'confirmed';
                            } else if (uploadVal != null && normalize(uploadVal) === normalize(v)) {
                                nextState = 'upload';
                            } else {
                                nextState = 'edited';
                            }

                            patchAiFieldAndState(key, v, nextState);
                            setEditingKey(null);
                        }}
                    />
                ) : (
                    <>
                        <div className="host-ai-field-value">{displayValue || '—'}</div>
                        {fieldErrors[key] && <div className="drawer-field-error">{fieldErrors[key]}</div>}
                        <div className="host-ai-field-actions">
                            {showConfirm && (
                                <button
                                    type="button"
                                    className="btn host-ai-field-confirm"
                                    onClick={() => handleConfirm(key, field)}
                                >
                                    <FlatCheck />
                                    {' '}
                                    Confirm
                                </button>
                            )}
                            {canEditThisField && (
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        if (!dirtyFields) onRequestEditWithSeed?.();
                                        setEditingKey(key);
                                    }}
                                >
                                    Edit
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    };

    const confirmAllCount = fields.filter((field) => {
        const state = getFieldState(field.key);
        if (state === 'upload') return false;
        return state === 'pending' || editingKey === field.key;
    }).length;

    const pendingCount = fields.filter((f) => {
        const s = getFieldState(f.key);
        return s === 'pending' || editingKey === f.key;
    }).length;

    const handleConfirmAll = () => {
        const applyAll = (state) => {
            const next = {
                ...state,
                aiFields: { ...state.aiFields },
                aiFieldStates: { ...state.aiFieldStates },
            };
            for (const field of fields) {
                const fieldState = next.aiFieldStates[field.key]
                    ?? inferViewState(field, listing);
                if (fieldState === 'upload') continue;
                if (fieldState === 'pending' || editingKey === field.key) {
                    const value = Object.prototype.hasOwnProperty.call(next.aiFields, field.key)
                        ? next.aiFields[field.key]
                        : field.displayValue;
                    next.aiFields[field.key] = value;
                    next.aiFieldStates[field.key] = 'confirmed';
                }
            }
            return next;
        };

        if (!dirtyFields) {
            const seed = onRequestEditWithSeed?.();
            if (!seed) return;
            syncDirtyRef.current = applyAll(seed);
            setDirtyFields?.(syncDirtyRef.current);
        } else {
            safePatch(applyAll);
        }
        setEditingKey(null);
    };

    return (
        <div className="host-panel-section">
            <div className="host-panel-section-label">
                AI-enriched fields
                <span className="section-aux">
                    {pendingCount}
                    {' '}
                    of
                    {' '}
                    {fields.length}
                    {' '}
                    pending
                </span>
            </div>
            <div className="host-ai-section">
                <div className="host-ai-aggregate">
                    <div>
                        <div className="host-ai-aggregate-label">Status</div>
                        <div className="host-ai-aggregate-value">
                            {pendingCount === 0 ? 'All confirmed' : `${pendingCount} pending`}
                        </div>
                        <div className="host-ai-aggregate-sub">
                            Confirm or edit each field before submitting.
                        </div>
                    </div>
                    <div className="host-ai-aggregate-actions">
                        {confirmAllCount > 0 ? (
                            <button type="button" className="btn host-ai-field-confirm" onClick={handleConfirmAll}>
                                <FlatCheck />
                                {' '}
                                Confirm all (
                                {confirmAllCount}
                                )
                            </button>
                        ) : (
                            <button type="button" className="btn host-ai-field-confirm" disabled>
                                <FlatCheck />
                                {' '}
                                All confirmed
                            </button>
                        )}
                    </div>
                </div>
                <div className="host-ai-fields">
                    {fields.map(renderEditCard)}
                </div>
            </div>
        </div>
    );
}
